import React, { createContext, useContext, useState, useEffect } from 'react';
import { Query } from 'urql';
import * as Sentry from '@sentry/browser';
import OverlayLoader from '../components/common/OverlayLoader';
import TimeOutError from '../components/common/TimeOutError';
import { CURRENT_CONTEXT_QUERY } from '../graphql/queries';
import { identify as intercomIdentify } from '../utils/intercom';
import { identify as segmentIdentify } from '../utils/analytics';

interface CurrentContextInterface {
  customer: Billing.ICustomer | null;
  subscription: Billing.ISubscription | null;
  user: Billing.IUser | null;
  organisation: Billing.IOrganisation | null;
  product: Billing.IProduct | null;
}

const CurrentContext = createContext<CurrentContextInterface>({
  customer: null,
  subscription: null,
  user: null,
  organisation: null,
  product: null,
});

export const CurrentContextProvider: React.FunctionComponent = ({ children }) => {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimedOut(true);
    }, 15000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Query query={CURRENT_CONTEXT_QUERY}>
      {({ fetching, data, error }) => {
        if ((fetching || error) && timedOut) return <TimeOutError />;
        if (fetching || !data) return <OverlayLoader />;
        if (data.user) {
          Sentry.configureScope(scope => {
            scope.setUser({
              id: data.user.id,
              email: data.user.email,
            });
          });

          segmentIdentify(data.user.id, {
            name: `${data.user.firstName} ${data.user.lastName}`,
            email: data.user.email,
            isPaid: !!data.subscription,
            currentPlan: data.organisation.product.name,
            trialEndsAt: data.organisation.trialEndsAt,
            couponCode: data.subscription ? data.subscription.couponCode : null,
          });

          intercomIdentify({
            id: data.user.id,
            name: `${data.user.firstName} ${data.user.lastName}`,
            email: data.user.email,
            isPaid: !!data.subscription,
            currentPlan: data.organisation.product.name,
            trialEndsAt: data.organisation.trialEndsAt,
            couponCode: data.subscription ? data.subscription.couponCode : null,
            intercomIdentityVerificationHash: data.user.intercomIdentityVerificationHash,
            language: data.user.language,
          });
        }

        const values = { ...data };
        if (data.organisation && data.organisation.product) {
          values.product = data.organisation.product;
        }
        return <CurrentContext.Provider value={values}>{children}</CurrentContext.Provider>;
      }}
    </Query>
  );
};

export const CurrentContextConsumer = CurrentContext.Consumer;

export const useCurrentContext = () => useContext(CurrentContext);

export default CurrentContext;
